import { ChangeEvent, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { PhoneOrEmailInputField, TranslationContext } from "@is/constants";
import { CheckoutInputFields, FieldState, InputTypes } from "@is/types";

type Props = {
  onChange: (name: CheckoutInputFields, type: string, value: string) => void;
  field: FieldState;
  phoneOrEmailField?: PhoneOrEmailInputField;
};

const useManageCheckoutFormField = ({
  field,
  onChange,
  phoneOrEmailField,
}: Props) => {
  const { t } = useTranslation(TranslationContext.Checkout);

  const fieldName = field.name as CheckoutInputFields;

  const label =
    field.name !== CheckoutInputFields.PhoneNumberOrEmail
      ? t(field.label)
      : undefined;

  const isPhoneSelectedFromPhoneOrEmail =
    phoneOrEmailField === PhoneOrEmailInputField.Phone &&
    field.name === CheckoutInputFields.PhoneNumberOrEmail;

  const type =
    field.type === "phone" || isPhoneSelectedFromPhoneOrEmail
      ? InputTypes.Tel
      : undefined;

  const value = !field.isDisabled ? field.value : undefined;

  const placeholder = field.isDisabled ? field.value : undefined;

  const handleTextInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { name, type, value } = event.target;

      onChange(name as CheckoutInputFields, type, value);
    },
    [onChange]
  );

  const handlePhoneInputChange = useCallback(
    (value: string) => {
      onChange(fieldName, "text", value);
    },
    [fieldName, onChange]
  );

  return {
    label,
    type,
    value,
    placeholder,
    handleTextInputChange,
    handlePhoneInputChange,
    isPhoneSelectedFromPhoneOrEmail,
  };
};

export default useManageCheckoutFormField;
